import { Database, LucideIcon, Route, Search, Users as UsersIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { Button } from './button'
import { cn } from '../../utils/cn'

interface EmptyStateProps {
  icon: LucideIcon
  title: string
  description: string
  action?: {
    label: string
    onClick: () => void
  }
  className?: string
  children?: ReactNode
}

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
  className,
  children
}: EmptyStateProps) => {
  return (
    <div className={cn('flex flex-col items-center justify-center py-10 px-4 text-center', className)}>
      <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/5 text-primary/70"><Icon aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} /></span>

      {/* Content */}
      <h3 className="mb-1.5 text-base font-semibold text-foreground">{title}</h3>
      <p className="mb-4 max-w-md text-sm leading-relaxed text-muted-foreground">{description}</p>

      {/* Action or custom children */}
      {action && (
        <Button onClick={action.onClick}>
          {action.label}
        </Button>
      )}
      {children}

    </div>
  )
}

// Preset empty states for common scenarios
export const NoUsersEmptyState = ({ onAddUser }: { onAddUser?: () => void }) => (
  <EmptyState
    icon={UsersIcon}
    title="No hikers yet"
    description="Your trail is empty. Start building your community by adding mobile users who will embark on hiking adventures."
    action={onAddUser ? {
      label: 'Invite First Hiker',
      onClick: onAddUser
    } : undefined}
  />
)

export const NoRoutesEmptyState = ({ onAddRoute }: { onAddRoute?: () => void }) => (
  <EmptyState
    icon={Route}
    title="No routes mapped"
    description="Chart your first trail. Create hiking routes that will guide adventurers through stunning landscapes."
    action={onAddRoute ? {
      label: 'Map Your First Trail',
      onClick: onAddRoute
    } : undefined}
  />
)

export const NoDataEmptyState = () => (
  <EmptyState
    icon={Database}
    title="No data available"
    description="This section is awaiting its first expedition. Data will appear here once activities begin."
  />
)

export const SearchEmptyState = ({ searchTerm }: { searchTerm: string }) => (
  <EmptyState
    icon={Search}
    title="No results found"
    description={`No matches for "${searchTerm}". Try adjusting your search or explore a different trail.`}
  />
)
